import { cacheEditorImages, type EditorImage } from '@/ui/editor/image-readiness'
import { useSetAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import { useLifecyclePersistence, useSyncLifecycleToExistingAtom } from '../../utilities'
import { ConformedCampaignLandingPage, MediaAsset } from '../../views'
import { useImageCacheWriter } from '../images'
import {
	landingPageAtom,
	landingPageHasGeneratedContentAtom,
	landingPageImageHistoryAtom,
	landingPageSavingAtom,
	landingPageStatusAtom,
	useLandingPageListener,
} from '../state'

export type UseLandingPageEditorArguments = {
	landingPage?: ConformedCampaignLandingPage

	// should reflect if the landing page's query is pending
	isPending: boolean

	// should reflect if the landing page's data indicate it's still being generated
	isGenerating: boolean

	// should reflect if a chat tool is currently executing on the landing page
	isToolExecuting?: boolean

	//
	imagesHistory?: Array<MediaAsset>

	// pass in the existing image, if present. This doesn't affect the actual
	// bound image; it's just for caching the image URL.
	image?: EditorImage

	// callback to handle landing page changes; THIS SHOULD BE DEBOUNCED.
	onLandingPageChange: (value: ConformedCampaignLandingPage) => void

	// optional version to prevent stale data syncing
	landingPageVersion?: number | string | Date | null
}

export function useLandingPageEditor(args: UseLandingPageEditorArguments) {
	const {
		landingPage,
		isPending,
		isGenerating,
		isToolExecuting = false,
		imagesHistory,
		image,
		onLandingPageChange,
		landingPageVersion,
	} = args

	const { cacheImageUrl } = useImageCacheWriter()

	// Use lifecycle persistence
	const landingPageLifecycle = useLifecyclePersistence<ConformedCampaignLandingPage>({
		data: landingPage ?? undefined,
		isIdle: (d) => d == undefined,
		isPending: () => isPending,
		isGenerating: () => isGenerating,
		isToolExecuting,
		save: async (landingPageData) => {
			await onLandingPageChange(landingPageData)
		},
		debounceMs: 800,
		getVersion: () => landingPageVersion ?? null,
		debug: false,
		name: 'useLandingPageEditor',
	})

	// Sync to existing atoms
	useSyncLifecycleToExistingAtom(
		landingPageLifecycle,
		{
			dataAtom: landingPageAtom,
			useDataListener: useLandingPageListener,
			lifecycleAtom: landingPageStatusAtom,
			persistenceAtom: landingPageSavingAtom,
		},
	)

	// Handle image caching
	const setLandingPageImageHistory = useSetAtom(landingPageImageHistoryAtom)
	const setHasGeneratedContent = useSetAtom(landingPageHasGeneratedContentAtom)
	const lastLandingPageIdRef = useRef<string | undefined>(landingPageLifecycle.data?.id)
	useEffect(() => {
		cacheEditorImages(image, imagesHistory, cacheImageUrl)
		if (imagesHistory) {
			setLandingPageImageHistory(imagesHistory)
		}
	}, [imagesHistory, setLandingPageImageHistory, cacheImageUrl, image])

	const landingPageId = landingPageLifecycle.data?.id
	useEffect(() => {
		if (lastLandingPageIdRef.current !== landingPageId) {
			lastLandingPageIdRef.current = landingPageId
			setHasGeneratedContent(false)
		}
	}, [landingPageId, setHasGeneratedContent])

	useEffect(() => {
		if (!landingPageLifecycle.data) {
			setHasGeneratedContent(false)
			return
		}

		if (
			(landingPageLifecycle.lifecycle === 'ready' || landingPageLifecycle.lifecycle === 'tool-executing')
			&& !!landingPageLifecycle.data?.body
		) {
			setHasGeneratedContent(true)
			return
		}

		if (landingPageLifecycle.lifecycle === 'idle' || landingPageLifecycle.lifecycle === 'pending') {
			setHasGeneratedContent(false)
			return
		}
	}, [landingPageLifecycle.data, landingPageLifecycle.lifecycle, setHasGeneratedContent])
}
